/**
 * English Translations
 */

export const en = {
  // Common
  continue: "Continue",
  back: "← Back",
  next: "Next",
  previous: "Previous",
  submit: "Submit",
  cancel: "Cancel",
  save: "Save",
  edit: "Edit",
  delete: "Delete",
  close: "Close",
  loading: "Loading...",
  error: "Error",
  success: "Success",
  warning: "Warning",
  info: "Information",
  required: "Required",
  optional: "Optional",
  
  // Property Valuation Result Storytelling Widget
  // Introduction Section
  propertyStoryIntro: "Scroll down to discover information about your property",
  
  // Chapter 1: The Property
  theProperty: "The Property",
  propertyDescription: "Every property has a story. This {living_area}m² apartment in {city} has been standing since {year}.",
  propertyProfile: "Property Profile",
  propertyType: "Property Type",
  rooms: "Rooms",
  historicalContext: "Historical Context",
  yearBuilt: "Year Built",
  currentCondition: "Current Condition",
  parking: "Parking",
  
  // Chapter 2: The Location
  theLocation: "The Location",
  locationDescription: "Location is often said to be the most important factor in real estate valuation. Let's explore what makes this property's location special.",
  landInformation: "Land Information",
  landType: "Land Type",
  egridNumber: "EGRID Number",
  zoning: "Zoning",
  inForceSince: "In force since",
  
  // Map Controls
  streetView: "Street View",
  satelliteView: "Satellite View",
  priceHeatmap: "Price Heatmap",
  zoningPlan: "Zoning Plan",
  mapNotAvailable: "Interactive map not available",
  mapLoadError: "Error loading map",
  
  // Chapter 3: The Market
  theMarket: "The Market",
  marketDescription: "Understanding the broader market context helps put your property's value in perspective.",
  pricePerM2Comparison: "Price per m² Comparison",
  medianPriceComparison: "Median Price Comparison",
  areaVsRegion: "Your area vs. Geneva Region",
  yourArea: "Your Area",
  genevaRegion: "Geneva Region",
  localMarketTrends: "Local Market Trends",
  averagePrice: "Q1 2025 Average Price",
  transactions: "Q1 2025 Transactions",
  priceTrend: "Price Trend (2023-2025)",
  regionalContext: "Regional Context",
  regionAverage: "Geneva Region Average",
  regionalTransactions: "Regional Transactions",
  regionalTrend: "Regional Trend",
  
  // Chapter 4: The Neighborhood
  theNeighborhood: "The Neighborhood",
  neighborhoodDescription: "A property's value is greatly influenced by what surrounds it. From cultural amenities to everyday necessities, let's explore what makes this location special.",
  nearbyPoi: "Nearby Points of Interest",
  artCulture: "Art & Culture",
  cafe: "Cafe",
  financial: "Financial",
  publicUtility: "Public Utility",
  environmentalFactors: "Environmental Factors",
  roadNoiseDay: "Road Noise (Day)",
  roadNoiseNight: "Road Noise (Night)",
  railwayNoiseDay: "Railway Noise (Day)",
  railwayNoiseNight: "Railway Noise (Night)",
  veryHigh: "Very High",
  high: "High",
  moderate: "Moderate",
  low: "Low",
  veryLow: "Very Low",
  neighborhoodScores: "Neighborhood Quality Scores",
  neighborhoodQualityAssessment: "Neighborhood Quality Assessment",
  neighborhoodQuality: "Neighborhood Quality",
  yourProperty: "Your Property",
  genevaAverage: "Geneva Average",
  shopping: "Shopping",
  grocery: "Grocery",
  cultureEntertainment: "Culture and Entertainment",
  diningDrinking: "Dining and Drinking",
  education: "Education",
  wellness: "Wellness",
  healthcare: "Healthcare",
  transport: "Transport",
  basicAmenities: "Basic Amenities",
  outstandingCategories: "Outstanding Categories",
  cultureEntertainmentDesc: "10/10 - Access to museums, theaters, and cultural venues",
  diningDrinkingDesc: "10/10 - High quality restaurants and cafes nearby",
  educationDesc: "10/10 - Excellent schools and educational facilities",
  areasConsideration: "Areas for Consideration",
  transportDesc: "7/10 - Good but limited transportation options",
  healthcareDesc: "6/10 - Some healthcare facilities nearby but limited specialties",
  
  // Chapter 5: The Value
  theValue: "The Value",
  valueDescription: "Now that we've explored the property, its location, the market context, and the neighborhood quality, we can present a comprehensive valuation based on all these factors.",
  goodConfidence: "Good Confidence",
  mediumConfidence: "Medium Confidence",
  lowConfidence: "Low Confidence",
  priceAnalysis: "Price Analysis",
  pricePerM2: "Price Range per m²",
  
  // Property Valuation Result MapDashboard Widget
  price: "Price",
  zoning: "Zoning",
  noise: "Noise",
  pricePerSquareMeter: "Price per m²",
  estimatedValueRange: "Estimated Value Range",
  confidence: "Confidence",
  fullReport: "Full Report",
  share: "Share",
  propertyDetails: "Property Details",
  propertyInformation: "Property Information",
  livingArea: "Living Area",
  yearBuilt: "Year Built",
  marketAnalysis: "Market Analysis",
  avgPricePerSquareMeter: "Avg. Price/m²",
  annualTrend: "Annual Trend",
  energyEnvironment: "Energy & Environment",
  energyRating: "Energy Rating",
  noiseLevel: "Noise Level",
  pointsOfInterest: "Points of Interest",
  low: "Low",
  medium: "Medium",
  high: "High",
  
  // Property Valuation Widget
  // Step 1: Address Input
  whereProperty: "Where is your property located?",
  enterAddress: "Enter your address",
  
  // Step 2: Location with Map
  isThisProperty: "Is this your property?",
  confirmAddress: "Confirm Address",
  thisIsMyProperty: "This is my property",
  confirmLocation: "Confirm Location",
  
  // Step 3b: Apartment Selection
  selectApartment: "Select your apartment",
  customApartment: "Custom Apartment",
  enterCustomDetails: "Enter your apartment details manually",
  floor: "Floor",
  
  // Step 4: Property Details
  tellAboutProperty: "Tell us about your property",
  bedrooms: "Number of rooms",
  bathrooms: "Number of bathrooms",
  livingArea: "Living area (m²)",
  landArea: "Land area (m²)",
  exactBedrooms: "Exact number of rooms",
  exactBathrooms: "Exact number of bathrooms",
  condition: "Condition",
  needsRenovation: "Needs renovation",
  wellMaintained: "Well maintained",
  newOrRenovated: "New or recently renovated",
  constructionYear: "Construction year",
  regblPrefillData: "We prefill data using the Federal Register of Buildings and Dwellings when we can. Please check the details.",
  
  // Step 5: Valuation Purpose
  valuationPurpose: "What's the purpose of your valuation?",
  sellingSoon: "Selling Soon",
  justCurious: "Just Curious",
  buyingSoon: "Buy Soon",
  refinancing: "Refinancing",
  whenSelling: "When are you planning to sell?",
  asap: "As soon as possible",
  within3months: "Within 3 months",
  threeToSixMonths: "3-6 months",
  sixToTwelveMonths: "6-12 months",
  justExploring: "Just exploring options",
  whatPrice: "What price do you estimate this property?",
  helpUnderstand: "This helps us understand your expectations but does not affect the valuation.",
  
  // Step 6: Loading/Processing
  processingData: "Processing your property data",
  pleaseWait: "Please wait while our model analyzes your property...",
  analyzingLocation: "Analyzing location data",
  findingComparables: "Finding comparable properties",
  calculatingTrends: "Calculating market trends",
  finalizingValuation: "Finalizing valuation",
  
  // Step 7: Contact Information
  valuationReady: "Your valuation is ready!",
  provideContact: "Please provide your contact details to receive your property valuation report",
  fullName: "Full Name",
  yourName: "Your name",
  emailAddress: "Email Address",
  yourEmail: "Your email",
  phoneNumber: "Phone Number",
  yourPhone: "Your phone number",
  title: "Title",
  mr: "Mr.",
  mrs: "Mrs.",
  ms: "Ms.",
  firstName: "First Name",
  lastName: "Last Name",
  comment: "Additional comments (optional)",
  yourComment: "Tell us more about your needs",
  agreeReceive: "I agree to be contacted questions or additional information and confirm that I have had the opportunity to review ",
  privacyPolicy: "the privacy policy",
  getValuation: "Get My Valuation",
  
  // Step 8: SMS Verification
  verifyPhone: "Verify your phone number",
  sentCode: "We've sent a 4-digit code to your phone. Please enter it below to verify your phone number.",
  didntReceive: "Didn't receive a code?",
  resendCode: "Resend code",
  verifyView: "Verify & View Results",
  
  // Step 8b: SMS code verification
  processingSmsCode: "Processing SMS code",
  pleaseWaitSmsCode: "Please wait while we verify your phone number...",
  
  // Step 9: Appraisal Result
  propertyValuation: "Your Property Valuation",
  estimatedRange: "Estimated range:",
  confidence: "Confidence:",
  medium: "Medium",
  valuationBased: "This valuation is based on recent comparable sales, market trends, and the property details you provided.",
  downloadReport: "See Report",
  contactAgent: "Contact Agent",
  startNew: "Start a new valuation",
  
  // Validation messages
  invalidEmail: "Please enter a valid email address",
  invalidPhone: "Please enter a valid phone number",
  requiredField: "This field is required",
  minCharacters: "Please enter at least {{min}} characters",
  maxCharacters: "Please enter no more than {{max}} characters",
  minValue: "Please enter a value greater than or equal to {{min}}",
  maxValue: "Please enter a value less than or equal to {{max}}",
  
  // Error messages
  errorFetchingAddresses: "Error fetching addresses. Please try again.",
  errorFetchingBuilding: "Error fetching building details. Please try again.",
  errorVerifyingCode: "Error verifying code. Please try again.",
  errorSubmittingValuation: "Error submitting valuation. Please try again.",
  
  // Property types
  apartment: "Apartment",
  house: "House",
  chalet: "Chalet",
  duplex: "Duplex",
  villa: "Villa",
  
  // Room formatting
  roomsFormat: "{{rooms}} rooms, {{bathrooms}} bathroom{{bathrooms_plural}}",
  bathrooms_plural_0: "s",
  bathrooms_plural_1: "",
  bathrooms_plural_n: "s",
  
  // Property conditions (matching API values)
  good_condition: "Good condition",
  needs_renovation: "Needs renovation",  
  new_renovated: "New or recently renovated",
};